import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  FileText, 
  Upload, 
  Calendar, 
  Building, 
  ExternalLink, 
  Download, 
  FileUp, 
  Check, 
  Send, 
  CheckCircle, 
  ShieldCheck, 
  KeyRound 
} from 'lucide-react';
import Modal from '../components/Modal';
import SearchableSelect from '../components/SearchableSelect';
import './FormPage.css';
import { API_BASE_URL, maskCnpjCpf } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function SupplierInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Form State (Cadastro / Edição)
  const [formData, setFormData] = useState({
    id: null,
    fornecedor_id: '',
    fornecedor_nome: '',
    data_contabilizacao: new Date().toISOString().split('T')[0],
    numero_nota: '',
    chave_acesso: '',
    observacoes: '',
    arquivo_nome_original: '',
    arquivo_path: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Modal State para "Informar para Receita" (Manifestação do Destinatário)
  const [isReceitaModalOpen, setIsReceitaModalOpen] = useState(false);
  const [receitaTarget, setReceitaTarget] = useState(null);
  const [receitaChave, setReceitaChave] = useState('');
  const [receitaTpEvento, setReceitaTpEvento] = useState('210200');
  const [isSendingReceita, setIsSendingReceita] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchInvoices();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores_notas.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setInvoices(data);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Erro ao buscar notas fiscais de fornecedores:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setSuppliers(data);
      }
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
    }
  };

  const openNewModal = () => {
    setFormData({
      id: null,
      fornecedor_id: '',
      fornecedor_nome: '',
      data_contabilizacao: new Date().toISOString().split('T')[0],
      numero_nota: '',
      chave_acesso: '',
      observacoes: '',
      arquivo_nome_original: '',
      arquivo_path: ''
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      fornecedor_id: item.fornecedor_id || '',
      fornecedor_nome: item.fornecedor_nome || '',
      data_contabilizacao: item.data_contabilizacao || new Date().toISOString().split('T')[0],
      numero_nota: item.numero_nota || '',
      chave_acesso: item.chave_acesso || '',
      observacoes: item.observacoes || '',
      arquivo_nome_original: item.arquivo_nome_original || '',
      arquivo_path: item.arquivo_path || ''
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      'Confirmação de Exclusão', 
      'Tem certeza que deseja excluir esta nota fiscal? O arquivo correspondente também será apagado.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores_notas.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchInvoices();
      } else {
        showAlert('Aviso', result.error || 'Erro ao excluir nota fiscal');
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      showAlert('Erro', 'Ocorreu um erro ao tentar excluir o registro.');
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);

      // Tentar extrair 44 dígitos do nome do arquivo
      const nameMatch = file.name.match(/(\d{44})/);
      if (nameMatch && !formData.chave_acesso) {
        setFormData(prev => ({ ...prev, chave_acesso: nameMatch[1] }));
      }

      // Se for arquivo XML, ler conteúdo para extrair automaticamente a Chave de Acesso e o Nº da Nota
      if (file.name.toLowerCase().endsWith('.xml')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const content = ev.target?.result || '';
          if (typeof content === 'string') {
            const chMatch = content.match(/<chNFe>(\d{44})<\/chNFe>/) || content.match(/Id="NFe(\d{44})"/);
            const numMatch = content.match(/<nNF>(\d+)<\/nNF>/);
            setFormData(prev => ({
              ...prev,
              chave_acesso: chMatch ? chMatch[1] : prev.chave_acesso,
              numero_nota: (!prev.numero_nota && numMatch) ? numMatch[1] : prev.numero_nota
            }));
          }
        };
        reader.readAsText(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fornecedor_id) {
      showAlert('Campo Obrigatório', 'Por favor, selecione um fornecedor na lista.');
      return;
    }

    if (!formData.data_contabilizacao) {
      showAlert('Campo Obrigatório', 'Por favor, informe a data de contabilização.');
      return;
    }

    const cleanChave = (formData.chave_acesso || '').replace(/\D/g, '');
    if (cleanChave.length > 0 && cleanChave.length !== 44) {
      showAlert('Chave Inválida', 'A Chave de Acesso da NF-e, quando preenchida, deve conter 44 dígitos numéricos.');
      return;
    }

    setIsSaving(true);

    try {
      const formPayload = new FormData();
      if (formData.id) formPayload.append('id', formData.id);
      formPayload.append('fornecedor_id', formData.fornecedor_id);
      formPayload.append('fornecedor_nome', formData.fornecedor_nome);
      formPayload.append('data_contabilizacao', formData.data_contabilizacao);
      formPayload.append('numero_nota', formData.numero_nota || '');
      formPayload.append('chave_acesso', cleanChave);
      formPayload.append('observacoes', formData.observacoes || '');

      if (selectedFile) {
        formPayload.append('arquivo', selectedFile);
      }

      const response = await fetch(`${API_BASE_URL}/fornecedores_notas.php`, {
        method: 'POST',
        body: formPayload
      });

      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchInvoices();
      } else {
        showAlert('Aviso', result.error || 'Erro ao salvar nota fiscal.');
      }
    } catch (error) {
      console.error('Erro ao salvar nota:', error);
      showAlert('Erro', 'Ocorreu uma falha ao enviar a nota fiscal.');
    } finally {
      setIsSaving(false);
    }
  };

  // Abrir modal para Informar Recebimento para a Receita Federal
  const openReceitaModal = (item) => {
    setReceitaTarget(item);
    setReceitaChave((item.chave_acesso || '').replace(/\D/g, ''));
    setReceitaTpEvento('210200');
    setIsReceitaModalOpen(true);
  };

  const handleConfirmInformarReceita = async (e) => {
    e.preventDefault();
    if (!receitaTarget) return;

    const cleanChave = (receitaChave || '').replace(/\D/g, '');
    if (cleanChave.length !== 44) {
      showAlert('Chave de Acesso Obrigatória', 'Informe os 44 dígitos da Chave de Acesso da NF-e para transmitir a confirmação de recebimento à Receita Federal.');
      return;
    }

    setIsSendingReceita(true);
    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores_notas.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'informar_receita',
          id: receitaTarget.id,
          chave_acesso: cleanChave,
          tp_evento: Number(receitaTpEvento)
        })
      });

      const result = await response.json();
      if (result.success) {
        setIsReceitaModalOpen(false);
        fetchInvoices();
        showAlert(
          'Informado para a Receita Federal!',
          `${result.motivo_receita}\n\nChave de Acesso: ${result.chave_acesso}\nProtocolo SEFAZ: ${result.protocolo_receita}`
        );
      } else {
        showAlert('Aviso da Receita / SEFAZ', result.error || 'Não foi possível registrar a manifestação na Receita Federal.');
      }
    } catch (error) {
      console.error('Erro ao informar Receita:', error);
      showAlert('Erro', 'Falha de comunicação ao tentar informar a Receita Federal.');
    } finally {
      setIsSendingReceita(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Filtragem
  const filteredInvoices = invoices.filter(item => {
    const term = searchTerm.toLowerCase();
    const supName = (item.fornecedor_nome || '').toLowerCase();
    const nfNum = (item.numero_nota || '').toLowerCase();
    const chave = (item.chave_acesso || '').toLowerCase();
    const dt = (item.data_contabilizacao || '').toLowerCase();
    const dtDisplay = formatDateDisplay(item.data_contabilizacao);
    const cnpj = (item.fornecedor_cnpj || '').toLowerCase();
    return (
      supName.includes(term) || 
      nfNum.includes(term) || 
      chave.includes(term) ||
      dt.includes(term) || 
      dtDisplay.includes(term) ||
      cnpj.includes(term)
    );
  });

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage) || 1;
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="page-container animate-fade-in">
      {/* CABEÇALHO */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Notas Fiscais de Fornecedores</h1>
          <p className="page-subtitle">Armazenamento, consulta e confirmação de recebimento junto à Receita Federal (SEFAZ)</p>
        </div>
        <button 
          className="btn-primary" 
          onClick={openNewModal}
          style={{ width: 'auto', padding: '0.65rem 1.25rem' }}
        >
          <Plus size={18} /> Subir Nota Fiscal
        </button>
      </div>

      {/* CARD PRINCIPAL COM FILTRO E LISTAGEM */}
      <div className="card list-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="search-bar" style={{ width: '100%', maxWidth: '420px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar por fornecedor, nº da nota, chave ou data..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Total de notas: <strong style={{ color: 'var(--text-main)' }}>{filteredInvoices.length}</strong>
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fornecedor</th>
                <th>Data de Contabilização</th>
                <th>Nº da Nota / Chave</th>
                <th>Arquivo Anexo</th>
                <th>Receita Federal</th>
                <th style={{ minWidth: '220px', maxWidth: '340px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((item) => {
                const isInformado = item.status_receita === 'INFORMADO';
                return (
                  <tr key={item.id}>
                    {/* FORNECEDOR */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          backgroundColor: 'var(--primary-light)',
                          color: 'var(--primary-color)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Building size={16} />
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                            {item.fornecedor_nome}
                          </div>
                          {item.fornecedor_cnpj && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                              CNPJ: {maskCnpjCpf(item.fornecedor_cnpj)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* DATA DE CONTABILIZAÇÃO */}
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-main)', fontWeight: '500' }}>
                        <Calendar size={14} className="text-muted" />
                        {formatDateDisplay(item.data_contabilizacao)}
                      </div>
                    </td>

                    {/* NÚMERO DA NOTA / CHAVE */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {item.numero_nota ? (
                          <span className="badge" style={{ backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', width: 'fit-content' }}>
                            NF #{item.numero_nota}
                          </span>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>Nº não informado</span>
                        )}
                        {item.chave_acesso && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }} title={`Chave de Acesso: ${item.chave_acesso}`}>
                            Chave: {item.chave_acesso.substring(0, 10)}...{item.chave_acesso.substring(38)}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* ARQUIVO / NOTA */}
                    <td>
                      {item.arquivo_path ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            onClick={() => window.open(`${API_BASE_URL}/fornecedores_notas.php?action=view&id=${item.id}`, '_blank')}
                            title="Visualizar arquivo da nota fiscal"
                            style={{
                              height: '28px',
                              padding: '0 0.65rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              borderRadius: '6px',
                              border: '1px solid #38bdf8',
                              backgroundColor: '#e0f2fe',
                              color: '#0284c7',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <FileText size={13} />
                            {item.arquivo_nome_original ? (
                              <span style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.arquivo_nome_original}
                              </span>
                            ) : 'Ver Nota'}
                          </button>
                          <a
                            href={`${API_BASE_URL}/fornecedores_notas.php?action=download&id=${item.id}`}
                            download
                            title="Baixar arquivo"
                            className="btn-icon"
                            style={{ width: '28px', height: '28px' }}
                          >
                            <Download size={14} />
                          </a>
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>Sem anexo</span>
                      )}
                    </td>

                    {/* STATUS RECEITA FEDERAL */}
                    <td>
                      {isInformado ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                          <span
                            className="badge badge-success"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', width: 'fit-content', cursor: 'pointer' }}
                            onClick={() => showAlert(
                              'Comprovante de Manifestação SEFAZ',
                              `Fornecedor: ${item.fornecedor_nome}\nChave de Acesso: ${item.chave_acesso || 'Não informada'}\nProtocolo: ${item.protocolo_receita || '-'}\nData/Hora: ${item.data_manifestacao || '-'}\n\n${item.motivo_receita || 'Recebimento confirmado junto à Receita Federal.'}`
                            )}
                            title="Clique para ver detalhes do protocolo SEFAZ"
                          >
                            <CheckCircle size={12} /> Informada
                          </span>
                          {item.protocolo_receita && (
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                              Prot: {item.protocolo_receita}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="badge badge-warning" style={{ width: 'fit-content' }}>
                          Pendente
                        </span>
                      )}
                    </td>

                    {/* AÇÕES */}
                    <td style={{ minWidth: '220px', maxWidth: '340px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {!isInformado && (
                          <button
                            type="button"
                            style={{
                              height: '28px',
                              padding: '0 0.6rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              borderRadius: '6px',
                              border: '1px solid #4ade80',
                              backgroundColor: '#dcfce7',
                              color: '#15803d',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.3rem',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease-in-out'
                            }}
                            onClick={() => openReceitaModal(item)}
                            title="Informar recebimento da nota fiscal para a Receita Federal (Manifestação do Destinatário)"
                          >
                            <Send size={13} /> Informar para Receita
                          </button>
                        )}

                        <button
                          style={{
                            height: '28px',
                            padding: '0 0.55rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #fbbf24',
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease-in-out'
                          }}
                          onClick={() => handleEdit(item)}
                          title="Editar Nota Fiscal"
                        >
                          <Edit2 size={13} /> Editar
                        </button>

                        <button
                          style={{
                            height: '28px',
                            padding: '0 0.55rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #f87171',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease-in-out'
                          }}
                          onClick={() => handleDelete(item.id)}
                          title="Excluir Nota Fiscal"
                        >
                          <Trash2 size={13} /> Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {paginatedInvoices.length === 0 && !loading && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    Nenhuma nota fiscal encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINAÇÃO */}
        {filteredInvoices.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Exibindo {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredInvoices.length)} de {filteredInvoices.length} notas fiscais
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className="btn-outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.85rem',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.85rem', fontWeight: '500', padding: '0 0.5rem', color: 'var(--text-main)' }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                className="btn-outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.85rem',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL PARA SUBIR / EDITAR NOTA FISCAL */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Nota Fiscal de Fornecedor" : "Subir Nota Fiscal de Fornecedor"}
        maxWidth="620px"
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
          
          {/* FORNECEDOR COM SEARCHABLE SELECT (INPUT COM SUGESTÕES) */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Building size={15} color="var(--primary-color)" />
              Fornecedor <span className="text-danger">*</span>
            </label>
            <SearchableSelect
              options={suppliers}
              value={formData.fornecedor_id}
              onChange={(val) => {
                const found = suppliers.find(s => String(s.id) === String(val));
                setFormData(prev => ({
                  ...prev,
                  fornecedor_id: val,
                  fornecedor_nome: found ? found.fornecedor_nome : ''
                }));
              }}
              placeholder="Digite para buscar fornecedor por nome ou CNPJ..."
              getOptionValue={s => s.id}
              getOptionLabel={s => `${s.fornecedor_nome}${s.fornecedor_cnpj ? ` (${maskCnpjCpf(s.fornecedor_cnpj)})` : ''}`}
              renderOption={s => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>{s.fornecedor_nome}</span>
                  {s.fornecedor_cnpj && (
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      CNPJ: {maskCnpjCpf(s.fornecedor_cnpj)}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {/* DATA DE CONTABILIZAÇÃO */}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Calendar size={15} color="var(--primary-color)" />
                Data de Contabilização <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                name="data_contabilizacao"
                value={formData.data_contabilizacao}
                onChange={(e) => setFormData(prev => ({ ...prev, data_contabilizacao: e.target.value }))}
                required
                className="standard-input"
              />
            </div>

            {/* NÚMERO DA NOTA (OPCIONAL) */}
            <div className="form-group">
              <label>Número da Nota / Série</label>
              <input
                type="text"
                name="numero_nota"
                placeholder="Ex: 001234"
                value={formData.numero_nota}
                onChange={(e) => setFormData(prev => ({ ...prev, numero_nota: e.target.value }))}
                className="standard-input"
              />
            </div>
          </div>

          {/* CHAVE DE ACESSO DA NF-e (44 DÍGITOS) */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <KeyRound size={15} color="var(--primary-color)" />
                Chave de Acesso da NF-e (44 dígitos)
              </span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: '500' }}>
                {(formData.chave_acesso || '').replace(/\D/g, '').length}/44 dígitos
              </span>
            </label>
            <input
              type="text"
              name="chave_acesso"
              maxLength={54}
              placeholder="Preenchida automaticamente ao subir o XML ou digite os 44 números..."
              value={formData.chave_acesso}
              onChange={(e) => setFormData(prev => ({ ...prev, chave_acesso: e.target.value.replace(/\D/g, '').slice(0, 44) }))}
              className="standard-input"
              style={{ fontFamily: 'monospace', letterSpacing: '0.03em' }}
            />
          </div>

          {/* ARQUIVO DA NOTA FISCAL */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <FileUp size={15} color="var(--primary-color)" />
              Arquivo da Nota Fiscal (PDF, XML, Imagem)
            </label>

            {formData.arquivo_path && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#f8fafc',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '0.8rem',
                marginBottom: '0.35rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', overflow: 'hidden' }}>
                  <FileText size={16} color="var(--primary-color)" />
                  <span style={{ fontWeight: '500', color: 'var(--text-main)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {formData.arquivo_nome_original || 'Nota fiscal anexada'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => window.open(`${API_BASE_URL}/fornecedores_notas.php?action=view&id=${formData.id}`, '_blank')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-color)',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.2rem'
                  }}
                >
                  <ExternalLink size={12} /> Ver
                </button>
              </div>
            )}

            <div style={{
              border: '2px dashed var(--border-color)',
              borderRadius: '8px',
              padding: '1.1rem 1rem',
              textAlign: 'center',
              backgroundColor: selectedFile ? '#f0fdf4' : '#faf5ff',
              transition: 'var(--transition)',
              cursor: 'pointer',
              position: 'relative'
            }}>
              <input
                type="file"
                accept=".pdf,.xml,.png,.jpg,.jpeg,.zip"
                onChange={handleFileChange}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                {selectedFile ? (
                  <>
                    <div style={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: '600', fontSize: '0.85rem' }}>
                      <Check size={18} /> Arquivo Selecionado: {selectedFile.name}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      ({(selectedFile.size / 1024).toFixed(1)} KB) - Clique para trocar
                    </span>
                  </>
                ) : (
                  <>
                    <Upload size={22} color="var(--primary-color)" />
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: 'var(--text-main)' }}>
                      {formData.arquivo_path ? 'Clique ou arraste para substituir o arquivo' : 'Clique ou arraste a nota fiscal aqui'}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Formatos aceitos: XML, PDF, PNG, JPG, ZIP (Ao enviar XML, a chave de 44 dígitos é lida automaticamente)
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* OBSERVAÇÕES */}
          <div className="form-group">
            <label>Observações Adicionais</label>
            <textarea
              name="observacoes"
              rows="2"
              placeholder="Ex: Referente ao pedido de compra nº 542..."
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              className="standard-input"
              style={{ height: 'auto', padding: '0.5rem', resize: 'vertical' }}
            />
          </div>

          {/* BOTÕES DE AÇÃO */}
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            <button
              type="button"
              className="btn-outline"
              onClick={() => setIsModalOpen(false)}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              style={{ width: 'auto', padding: '0.6rem 1.35rem' }}
              disabled={isSaving}
            >
              {isSaving ? 'Salvando...' : (formData.id ? 'Atualizar Nota Fiscal' : 'Salvar Nota Fiscal')}
            </button>
          </div>

        </form>
      </Modal>

      {/* MODAL PARA INFORMAR RECEBIMENTO À RECEITA FEDERAL (MANIFESTAÇÃO DO DESTINATÁRIO) */}
      <Modal
        isOpen={isReceitaModalOpen}
        onClose={() => !isSendingReceita && setIsReceitaModalOpen(false)}
        title="Informar Recebimento para a Receita Federal"
        maxWidth="560px"
      >
        {receitaTarget && (
          <form onSubmit={handleConfirmInformarReceita} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '0.85rem 1rem',
              fontSize: '0.82rem',
              color: '#166534',
              display: 'flex',
              gap: '0.65rem',
              alignItems: 'flex-start'
            }}>
              <ShieldCheck size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Manifestação do Destinatário (SEFAZ Ambiente Nacional)</strong>
                <div style={{ marginTop: '0.2rem', color: '#15803d' }}>
                  Registra oficialmente junto à Receita Federal que a Alefolia recebeu a nota fiscal e a mercadoria emitida pelo fornecedor.
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              fontSize: '0.82rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.3rem'
            }}>
              <div><strong>Fornecedor:</strong> {receitaTarget.fornecedor_nome}</div>
              <div><strong>Data de Contabilização:</strong> {formatDateDisplay(receitaTarget.data_contabilizacao)}</div>
              {receitaTarget.numero_nota && <div><strong>Número da Nota:</strong> #{receitaTarget.numero_nota}</div>}
            </div>

            <div className="form-group">
              <label>Tipo de Evento na Receita Federal</label>
              <select
                className="standard-input"
                value={receitaTpEvento}
                onChange={(e) => setReceitaTpEvento(e.target.value)}
              >
                <option value="210200">210200 - Confirmação da Operação (Mercadoria Recebida)</option>
                <option value="210210">210210 - Ciência da Emissão (Confirmar Ciência da Nota)</option>
              </select>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <KeyRound size={15} color="var(--primary-color)" />
                  Chave de Acesso da NF-e (44 dígitos) <span className="text-danger">*</span>
                </span>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: receitaChave.length === 44 ? '#16a34a' : 'var(--text-muted)'
                }}>
                  {receitaChave.length}/44 dígitos
                </span>
              </label>
              <input
                type="text"
                required
                placeholder="Digite ou cole os 44 números da Chave de Acesso do DANFE/XML..."
                value={receitaChave}
                onChange={(e) => setReceitaChave(e.target.value.replace(/\D/g, '').slice(0, 44))}
                className="standard-input"
                style={{ fontFamily: 'monospace', fontSize: '0.88rem', letterSpacing: '0.03em' }}
              />
            </div>

            <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
              <button
                type="button"
                className="btn-outline"
                onClick={() => setIsReceitaModalOpen(false)}
                disabled={isSendingReceita}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-success"
                style={{ width: 'auto', padding: '0.6rem 1.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                disabled={isSendingReceita}
              >
                <Send size={15} />
                {isSendingReceita ? 'Transmitindo para a SEFAZ...' : 'Confirmar e Informar para Receita'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
